import * as React from 'react'

import {
  Box,
  Filters,
  SearchField,
  type SortDirection,
  Table,
  useToggle,
} from '@factorialco/deprecated-design-system'
import compact from 'lodash/compact'

import { observer } from 'mobx-react'

import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { applyFilters } from 'modules/core/lib/filters'
import FiltersForm from 'modules/core/lib/forms/Filters'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import type Employee from 'modules/employees/store/models/Employee'
import goals from 'modules/goals/store/collections/goals'
import type Goal from 'modules/goals/store/models/Goal'
import type GoalConfig from 'modules/goals/store/models/GoalConfig'

import Actions from './_Actions'
import Body from './_Body'
import Options, { GoalsFilters } from './_Filters'
import Header from './_Header'
import OwnersModal from './_OwnersModal'
import { filterByEmployee, filterBySearch, filterByStatus, sortGoals } from './filtering'

interface Props {
  goalConfig: GoalConfig
  showActions?: boolean
  canSeeClockedIn: boolean
  contractVersions: ContractVersions
}

const GoalTable = ({ goalConfig, showActions, canSeeClockedIn, contractVersions }: Props) => {
  const [isOpen, toggleOpen] = useToggle(false)
  const { search, onSearch } = useDebouncedSearch()
  const [direction, setDirection] = React.useState<SortDirection>('asc')
  const [attribute, setAttribute] = React.useState<string>('user_name')
  const [progressStatus, setProgressStatus] = React.useState<string>('')
  const [filterBy, setFilterBy] = React.useState<string>('')
  const [filteredGoals, setFilteredGoals] = React.useState<{ loading: boolean; value: Goal[] }>({
    loading: false,
    value: [],
  })

  const filtersForm = new GoalsFilters({
    progress_status: progressStatus,
  })

  const employeesFiltersForm = new FiltersForm({
    filterBy: filterBy,
  })

  const employees: Employee[] = compact(
    goalConfig.attributes.owner_ids.map((id: number) => users.get(id)?.employee)
  )

  React.useEffect(() => {
    const filterFn = async () => {
      setFilteredGoals({
        loading: true,
        value: [],
      })

      const filteredEmployees = await applyFilters(employeesFiltersForm, employees)
      const filteredBySearch = filterBySearch(search)
      const filteredByStatus = filterByStatus(filteredBySearch, progressStatus)

      const filteredByEmployee = filterByEmployee(filteredByStatus, filteredEmployees, filterBy)

      setFilteredGoals({
        loading: false,
        value: sortGoals(filteredByEmployee, attribute, direction),
      })
    }
    filterFn()
  }, [
    search,
    direction,
    attribute,
    goals.models.length,
    progressStatus,
    filterBy,
    employees.length,
  ])

  const onSort = (attribute: string, direction: SortDirection) => {
    setAttribute(attribute)
    setDirection(direction)
  }

  const onFilterStatus = (status: string) => setProgressStatus(status)
  const onFilterEmployee = (filterBy: string) => setFilterBy(filterBy)

  const FiltersOptions = () => (
    <Options
      filtersForm={filtersForm}
      employeesFiltersForm={employeesFiltersForm}
      onFilterStatus={onFilterStatus}
      onFilterEmployee={onFilterEmployee}
      canSeeClockedIn={canSeeClockedIn}
    />
  )

  const TableOptions = () => (
    <Box flexDirection="row" alignItems="center" gap="s8" flexGrow justifyContent="spaceBetween">
      <Box flexDirection="row" gap="s8" alignItems="center" height="full">
        <Filters
          label={i18n.t('surveys.goals.table.filter')}
          title={i18n.t('surveys.goals.show.table.filters.title')}
          description={i18n.t('surveys.goals.show.table.filters.description')}
          options={FiltersOptions}
        />
        <SearchField
          variant="big"
          search={search}
          onSearch={onSearch}
          placeholder={i18n.t('common.search')}
        />
      </Box>
      <Box flexGrow alignItems="flexEnd">
        <Actions showActions={showActions} onClick={toggleOpen(true)} goalConfig={goalConfig} />
      </Box>
    </Box>
  )

  return (
    <Box>
      <Table options={TableOptions} fullWidth>
        <Header
          goalConfig={goalConfig}
          onSort={onSort}
          attribute={attribute}
          direction={direction}
        />
        <Body
          filteredGoals={filteredGoals.value}
          goalConfig={goalConfig}
          showActions={showActions}
        />
      </Table>
      {isOpen && (
        <OwnersModal
          goalConfig={goalConfig}
          onClose={toggleOpen(false)}
          contractVersions={contractVersions}
        />
      )}
    </Box>
  )
}

export default observer(GoalTable)
